
import React, { useCallback, useEffect, useState } from "react";
import "../Style/PermissionDashboard.css";

const API_BASE_URL = "https://api.yellowmatrix.co.in";
const PER_PAGE = 5;

const ToggleSwitch = ({ checked, onChange, disabled = false }) => (
  <label className={`toggleSwitch-Permissions ${disabled ? "disabled" : ""}`}>
    <div className="toggleSwitch-wrapper-Permissions">
      <input
        type="checkbox"
        className="toggleSwitch-input-Permissions"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <div
        className={`toggleSwitch-background-Permissions ${
          checked ? "checked" : ""
        }`}
      />
      <div
        className={`toggleSwitch-dot-Permissions ${checked ? "checked" : ""}`}
      />
    </div>
  </label>
);

const PERMISSION_PAGES = [
  {
    group: "Home",
    pages: [
      { code: "TRIPS", label: "Trips" },
      { code: "REPORTS", label: "Reports" },
    ],
  },
  {
    group: "Actions & Events",
    pages: [
      { code: "GPIO", label: "Mobilize/Immobilize" },
      { code: "ALERTS", label: "Alerts" },
      { code: "CONNECTIVITY", label: "Connectivity" },
    ],
  },
  {
    group: "Device Deployment",
    pages: [
      { code: "DEVICES", label: "Devices" },
      { code: "PROVISIONING", label: "Onboarding" },
      { code: "PARAMETER_OTA", label: "Parameter OTA" },
      { code: "FOTA", label: "FOTA" },
    ],
  },
  {
    group: "ECU Over Air Updates",
    pages: [{ code: "ECU_FOTA", label: "ECU FOTA" }],
  },
  {
    group: "Geo Services",
    pages: [{ code: "AOI", label: "AOI" }],
  },
  {
    group: "Manage",
    pages: [
      { code: "VEHICLES", label: "Vehicles" },
      { code: "USER_MANAGEMENT", label: "User Management" },
    ],
  },
];

export default function Permissions() {
  const [allUsers, setAllUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [userPermissions, setUserPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [pagination, setPagination] = useState({
    page: 1,
    per_page: PER_PAGE,
    total: 0,
    pages: 1,
    has_next: false,
    has_prev: false,
  });

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/customers`, {
        method: "GET",
        headers: getAuthHeader(),
      });

      const text = await response.text();
      const result = text ? JSON.parse(text) : null;

      if (!response.ok) {
        throw new Error(result?.message || "Failed to load customers");
      }

      setCustomers(Array.isArray(result) ? result : result?.data || []);
    } catch (error) {
      console.error("Fetch customers error:", error);
      setError(error.message || "Unable to load customers.");
    }
  }, [getAuthHeader]);

  const fetchPermissionPage = useCallback(
    async (page = 1) => {
      const response = await fetch(
        `${API_BASE_URL}/users/page-permissions?page=${page}&per_page=${PER_PAGE}`,
        {
          method: "GET",
          headers: getAuthHeader(),
        },
      );

      const text = await response.text();
      const result = text ? JSON.parse(text) : null;

      if (!response.ok) {
        throw new Error(result?.message || "Failed to load permissions");
      }

      if (!result || result.status !== "success") {
        throw new Error(result?.message || "Failed to load permissions");
      }

      return result;
    },
    [getAuthHeader],
  );

  const fetchAllUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const firstResult = await fetchPermissionPage(1);
      let collectedUsers = firstResult.data || [];

      const totalPages = firstResult.pagination?.pages || 1;

      if (totalPages > 1) {
        const requests = [];

        for (let page = 2; page <= totalPages; page++) {
          requests.push(fetchPermissionPage(page));
        }

        const results = await Promise.all(requests);

        results.forEach((result) => {
          if (result.data) {
            collectedUsers = [...collectedUsers, ...result.data];
          }
        });
      }

      const uniqueUsers = Array.from(
        new Map(collectedUsers.map((user) => [String(user.id), user])).values(),
      );

      setAllUsers(uniqueUsers);
      setUsers(uniqueUsers.slice(0, PER_PAGE));

      const permissionMap = {};

      uniqueUsers.forEach((user) => {
        permissionMap[String(user.id)] = new Set(user.pages || []);
      });

      setUserPermissions(permissionMap);

      setPagination({
        page: 1,
        per_page: PER_PAGE,
        total: uniqueUsers.length,
        pages: Math.max(1, Math.ceil(uniqueUsers.length / PER_PAGE)),
        has_next: uniqueUsers.length > PER_PAGE,
        has_prev: false,
      });
    } catch (error) {
      console.error("Fetch users error:", error);
      setError(error.message || "Unable to load permissions.");
    } finally {
      setLoading(false);
    }
  }, [fetchPermissionPage]);

  useEffect(() => {
    fetchAllUsers();
    fetchCustomers();
  }, [fetchAllUsers, fetchCustomers]);

  const getFilteredUsers = () => {
    if (!selectedCustomer) {
      return allUsers;
    }

    return allUsers.filter(
      (user) => String(user.customer_id) === String(selectedCustomer),
    );
  };

  const updateDisplayedUsers = (customerId, page = 1) => {
    let filteredUsers = allUsers;

    if (customerId) {
      filteredUsers = allUsers.filter(
        (user) => String(user.customer_id) === String(customerId),
      );
    }

    const total = filteredUsers.length;
    const pages = Math.max(1, Math.ceil(total / PER_PAGE));
    const validPage = Math.min(Math.max(page, 1), pages);
    const startIndex = (validPage - 1) * PER_PAGE;

    setUsers(filteredUsers.slice(startIndex, startIndex + PER_PAGE));

    setPagination({
      page: validPage,
      per_page: PER_PAGE,
      total,
      pages,
      has_next: validPage < pages,
      has_prev: validPage > 1,
    });
  };

  const handleCustomerChange = (event) => {
    const customerId = event.target.value;

    setSelectedCustomer(customerId);
    setSelectedUser("");
    setError("");
    setSuccessMessage("");

    updateDisplayedUsers(customerId, 1);
  };

  const handleUserChange = (event) => {
    const userId = event.target.value;

    setSelectedUser(userId);
    setError("");
    setSuccessMessage("");

    if (!userId) {
      updateDisplayedUsers(selectedCustomer, 1);
      return;
    }

    const selectedUserData = allUsers.find(
      (user) => String(user.id) === String(userId),
    );

    if (!selectedUserData) {
      setError("Selected user not found.");
      return;
    }

    setUsers([selectedUserData]);

    setPagination({
      page: 1,
      per_page: 1,
      total: 1,
      pages: 1,
      has_next: false,
      has_prev: false,
    });
  };

  const handleToggle = (userId, pageCode) => {
    const normalizedUserId = String(userId);

    setUserPermissions((previousPermissions) => {
      const currentPermissions = new Set(
        previousPermissions[normalizedUserId] || [],
      );

      if (currentPermissions.has(pageCode)) {
        currentPermissions.delete(pageCode);
      } else {
        currentPermissions.add(pageCode);
      }

      return {
        ...previousPermissions,
        [normalizedUserId]: currentPermissions,
      };
    });

    setError("");
    setSuccessMessage("");
  };

  const handleSaveChanges = async () => {
    if (users.length === 0) {
      return;
    }

    setSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      const usersToSave = selectedUser
        ? users.filter((user) => String(user.id) === String(selectedUser))
        : users;

      const normalUsers = usersToSave.filter(
        (user) => user.role !== "SuperAdmin",
      );

      for (const user of normalUsers) {
        const normalizedUserId = String(user.id);

        const permissions = Array.from(userPermissions[normalizedUserId] || []);

        const response = await fetch(
          `${API_BASE_URL}/users/${encodeURIComponent(
            normalizedUserId,
          )}/page-permissions`,
          {
            method: "PUT",
            headers: getAuthHeader(),
            body: JSON.stringify({
              customer_id: user.customer_id,
              pages: permissions,
            }),
          },
        );

        const text = await response.text();
        const result = text ? JSON.parse(text) : null;

        if (!response.ok) {
          throw new Error(
            `${user.username}: ${
              result?.message || "Failed to update permissions"
            }`,
          );
        }

        if (!result || result.status !== "success") {
          throw new Error(
            `${user.username}: ${
              result?.message || "Failed to update permissions"
            }`,
          );
        }

        setAllUsers((previousUsers) =>
          previousUsers.map((existingUser) =>
            String(existingUser.id) === normalizedUserId
              ? {
                  ...existingUser,
                  pages: permissions,
                }
              : existingUser,
          ),
        );
      }

      setSuccessMessage("Permissions updated successfully.");

      if (selectedUser) {
        setUsers((previousUsers) =>
          previousUsers.map((user) =>
            String(user.id) === String(selectedUser)
              ? {
                  ...user,
                  pages: Array.from(
                    userPermissions[String(selectedUser)] || [],
                  ),
                }
              : user,
          ),
        );
      } else {
        await fetchAllUsers();

        if (selectedCustomer) {
          updateDisplayedUsers(selectedCustomer, pagination.page);
        }
      }
    } catch (error) {
      console.error("Save permissions error:", error);
      setError(error.message || "Unable to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  const handlePreviousPage = () => {
    if (selectedUser || loading || saving) {
      return;
    }

    if (pagination.page <= 1) {
      return;
    }

    updateDisplayedUsers(selectedCustomer, pagination.page - 1);
  };

  const handleNextPage = () => {
    if (selectedUser || loading || saving) {
      return;
    }

    if (pagination.page >= pagination.pages) {
      return;
    }

    updateDisplayedUsers(selectedCustomer, pagination.page + 1);
  };

  const filteredUsersForDropdown = getFilteredUsers();

  return (
    <div className="container-Permissions">
      <div className="panel-Permissions">
        <div className="panel-header-Permissions">
          <h1 className="panel-title-Permissions">
            User Permissions Management
          </h1>

          <p className="panel-subtitle-Permissions">
            Manage page access and permissions for users based on their roles.
          </p>

          <hr className="line-Permissions" width="100%" />
        </div>

        <div
          style={{
            display: "flex",
            gap: "20px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <div className="permission-filter-group-Permissions">
            <label className="permission-filter-label-Permissions">
              Customer
            </label>

            <select
              className="permission-filter-select-Permissions"
              value={selectedCustomer}
              onChange={handleCustomerChange}
              disabled={loading || saving}
            >
              <option value="">Select Customer</option>

              {customers.map((customer) => (
                <option key={customer.CustomerID} value={customer.CustomerID}>
                  {customer.CustomerName}
                </option>
              ))}
            </select>
          </div>

          <div className="permission-filter-group-Permissions">
            <label className="permission-filter-label-Permissions">User</label>

            <select
              className="permission-filter-select-Permissions"
              value={selectedUser || ""}
              onChange={handleUserChange}
              disabled={!selectedCustomer || loading || saving}
            >
              <option value="">Select User</option>

              {filteredUsersForDropdown.map((user) => (
                <option key={String(user.id)} value={String(user.id)}>
                  {user.username}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className="error-message-Permissions">{error}</div>}

        {successMessage && (
          <div className="success-message-Permissions">{successMessage}</div>
        )}

        {loading ? (
          <div className="loading-Permissions">Loading permissions...</div>
        ) : users.length === 0 ? (
          <div className="loading-Permissions">No users found.</div>
        ) : (
          <>
            <div className="table-container-Permissions">
              <table className="table-Permissions">
                <thead className="table-head-Permissions">
                  <tr>
                    <th
                      scope="col"
                      className="table-header-cell-Permissions page-access-Permissions"
                    >
                      Page Access
                    </th>

                    {users.map((user) => (
                      <th
                        key={String(user.id)}
                        scope="col"
                        className="table-header-cell-Permissions"
                      >
                        <div>{user.username}</div>

                        <div
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "normal",
                            marginTop: "3px",
                          }}
                        >
                          {user.role}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {PERMISSION_PAGES.map((group) => (
                    <React.Fragment key={group.group}>
                      <tr className="permission-group-row-Permissions">
                        <th
                          colSpan={users.length + 1}
                          className="permission-group-title-Permissions"
                        >
                          {group.group}
                        </th>
                      </tr>

                      {group.pages.map((page) => (
                        <tr key={page.code} className="table-row-Permissions">
                          <th
                            scope="row"
                            className="table-header-cell-body-Permissions"
                          >
                            {page.label}
                          </th>

                          {users.map((user) => {
                            const isSuperAdmin = user.role === "SuperAdmin";

                            const normalizedUserId = String(user.id);

                            const isChecked =
                              isSuperAdmin ||
                              (userPermissions[normalizedUserId]?.has(
                                page.code,
                              ) ??
                                false);

                            return (
                              <td
                                key={`${normalizedUserId}-${page.code}`}
                                className="table-cell-Permissions"
                              >
                                <ToggleSwitch
                                  checked={isChecked}
                                  disabled={isSuperAdmin || saving}
                                  onChange={() =>
                                    handleToggle(user.id, page.code)
                                  }
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {!selectedUser && (
              <div className="pagination-container-Permissions">
                <button
                  disabled={!pagination.has_prev || loading || saving}
                  onClick={handlePreviousPage}
                >
                  Previous
                </button>

                <span>
                  Page {pagination.page} of {pagination.pages} (
                  {pagination.total} users)
                </span>

                <button
                  disabled={!pagination.has_next || loading || saving}
                  onClick={handleNextPage}
                >
                  Next
                </button>
              </div>
            )}

            <div className="panel-footer-Permissions">
              <button
                className="save-button-Permissions"
                onClick={handleSaveChanges}
                disabled={saving || loading}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
